import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { useApi } from '@/shared/api/useApi';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { Button } from '@/shared/components';

export function ConversationCases({
  conversationId,
  embedded = false,
}: {
  conversationId: string;
  /** In der Kontextspalte: ohne eigenen Rand, und ohne Fälle ein Satz statt nichts. */
  embedded?: boolean;
}) {
  const api = useApi();
  const { t } = useTranslation('pluginCases');
  const query = useQuery({
    queryKey: ['inbox', 'plugin-cases', conversationId],
    queryFn: () => api.pluginCases.get(conversationId),
    retry: false,
    refetchInterval: 15000,
  });
  const problem = useApiProblem(query.error);
  if (!query.isPending && !query.isError && !query.data?.length) {
    return embedded ? <p className="text-ink-soft text-sm">Keine offenen Fälle.</p> : null;
  }
  return (
    <section
      aria-label={t('title')}
      className={
        embedded ? undefined : 'border-line bg-card-alt flex-none border-b px-3 py-2 sm:px-6'
      }
    >
      {query.isPending ? (
        <p className="text-ink-soft text-sm">{t('loading')}</p>
      ) : query.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p role="alert" className="text-ink-soft text-sm">
            {t(problem?.status === 403 ? 'forbidden' : 'unavailable')}
          </p>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => void query.refetch()}
          >
            {t('retry')}
          </Button>
        </div>
      ) : (
        <ul className="divide-line max-h-44 divide-y overflow-y-auto">
          {query.data?.map((item) => (
            <li
              key={item.caseId}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1"
            >
              <div className="min-w-0">
                <p className="text-ink text-sm font-semibold">{t(`states.${item.state}`)}</p>
                <p className="text-ink-soft text-sm">{t(`reasons.${item.reason}`)}</p>
              </div>
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sage focus-visible:ring-sage inline-flex min-h-11 items-center gap-2 rounded-md text-sm font-semibold underline underline-offset-4 focus-visible:ring-2"
                >
                  {t('open')}
                  <ExternalLink size={15} aria-hidden />
                </a>
              ) : (
                <p className="text-ink-soft text-sm">{t('linkUnavailable')}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
